import type { Driver } from 'driver.js';
import {
    createWorkspaceWelcomeTourSteps,
    shouldOfferWorkspaceWelcomeTour,
    WORKSPACE_WELCOME_TOUR_ID,
    WORKSPACE_WELCOME_TOUR_VERSION,
} from '~/lib/workspaceTour';

let activeTour: Driver | null = null;

export function useWorkspaceTour() {
    const tenantStore = useTenantStore();
    const userStore = useUserStore();
    const isActive = useState<boolean>('workspace-welcome-tour-active', () => false);

    const canAutoStart = computed(() => shouldOfferWorkspaceWelcomeTour(
        userStore.user?.preferences,
        tenantStore.currentTenantId,
    ));

    async function saveOutcome(status: 'completed' | 'dismissed') {
        const tenantId = tenantStore.currentTenantId;
        if (!tenantId) return;

        try {
            await userStore.updateGuidedTourProgress(tenantId, WORKSPACE_WELCOME_TOUR_ID, {
                status,
                version: WORKSPACE_WELCOME_TOUR_VERSION,
            });
        } catch (error) {
            console.warn('Workspace tour progress could not be saved:', error);
        }
    }

    async function startWelcomeTour(options: { force?: boolean } = {}) {
        if (!import.meta.client || isActive.value) return false;
        if (!tenantStore.currentTenantId) return false;
        if (!options.force && !canAutoStart.value) return false;

        const mobile = !window.matchMedia('(min-width: 768px)').matches;
        const workspaceName = tenantStore.currentTenant?.name || 'your workspace';
        const steps = createWorkspaceWelcomeTourSteps({ mobile, workspaceName });
        const { driver } = await import('driver.js');
        let outcome: 'completed' | 'dismissed' | null = null;

        if (activeTour?.isActive()) {
            activeTour.destroy();
        }

        await nextTick();
        await new Promise<void>((resolve) => window.setTimeout(resolve, 220));

        activeTour = driver({
            steps,
            animate: true,
            smoothScroll: true,
            allowClose: true,
            allowScroll: false,
            allowKeyboardControl: true,
            overlayColor: '#090d16',
            overlayOpacity: 0.78,
            overlayClickBehavior: (_element, _step, { driver: tour }) => {
                outcome = 'dismissed';
                tour.destroy();
            },
            stagePadding: mobile ? 8 : 10,
            stageRadius: 16,
            popoverClass: 'vuey-workspace-tour',
            popoverOffset: 14,
            showProgress: true,
            progressText: '{{current}} of {{total}}',
            nextBtnText: 'Next',
            prevBtnText: 'Back',
            doneBtnText: 'Finish',
            skipMissingElement: true,
            waitForElement: 1200,
            disableActiveInteraction: true,
            onCloseClick: (_element, _step, { driver: tour }) => {
                outcome = 'dismissed';
                tour.destroy();
            },
            onDoneClick: (_element, _step, { driver: tour }) => {
                outcome = 'completed';
                tour.destroy();
            },
            onDestroyed: () => {
                isActive.value = false;
                activeTour = null;
                void saveOutcome(outcome || 'dismissed');
            },
        });

        isActive.value = true;
        activeTour.drive();
        return true;
    }

    function stopTour() {
        activeTour?.destroy();
    }

    onBeforeUnmount(() => {
        if (activeTour?.isActive()) {
            activeTour.destroy();
        }
    });

    return {
        canAutoStart,
        isActive: readonly(isActive),
        startWelcomeTour,
        stopTour,
    };
}
